import { useQuery } from '@apollo/client'
import { Box, Button } from '@factorialco/deprecated-design-system'
import { CompanyLinksHomeWidgetDocument } from 'generated/resources/graphql'
import { v4 as uuid } from 'uuid'

import Modal from 'modules/core/components/Modal'
import SortableLinkBoxList from 'modules/core/components/SortableLinkBoxList'
import { FormProvider } from 'modules/core/hooks/useForm'
import { useFormContext } from 'modules/core/hooks/useForm'
import { FieldArray } from 'modules/core/hooks/useForm'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import companyLinksCollection from 'modules/dashboard/store/collections/companyLinks'
import { FormValues } from 'modules/dashboard/widgets/CompanyLinks/forms'

import ListItem from './ListItem'

interface Props {
  handleClose: () => void
  handleCancel: () => void
}

const Content = ({ handleCancel }: Pick<Props, 'handleCancel'>) => {
  const form = useFormContext<FormValues>()

  return (
    <Modal
      illustration="control-navigation-adjust-sound"
      title={i18n.t('dashboard.company_links.modal.title')}
      description={i18n.t('dashboard.company_links.modal.description')}
      onClose={handleCancel}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Box flexDirection="row" gap="s8">
          <Button type="secondary" onClick={handleCancel}>
            {i18n.t('dashboard.company_links.modal.cancel')}
          </Button>

          <Button disabled={!form.isValid || form.isSubmitting} submit>
            {i18n.t('dashboard.company_links.modal.save')}
          </Button>
        </Box>
      }
    >
      <FieldArray name="links">
        {({ push, remove, replace }) => (
          <>
            {form.values.links.length > 0 ? (
              <SortableLinkBoxList
                items={form.values.links}
                onReorder={(items) => {
                  items.forEach((item, index) => replace(index, item))
                }}
                itemAsKey={(item) => item.id}
                renderItem={(link, index) => (
                  <ListItem
                    key={index}
                    index={index}
                    item={link}
                    onDelete={(index) => {
                      if (!link.draft) {
                        form.setValues({
                          ...form.values,
                          linkIdsToBeDeleted: [...form.values.linkIdsToBeDeleted, link.id],
                        })
                      }

                      remove(index)
                    }}
                    showPopoverOptions
                  />
                )}
              />
            ) : null}

            <Box flexDirection="row" gap="s8" paddingTop="s12">
              <Button
                type="secondary"
                onClick={() => {
                  push({ id: uuid(), name: '', url: '', draft: true })
                }}
                iconName="add"
              >
                {i18n.t('box_list.add_link')}
              </Button>
            </Box>
          </>
        )}
      </FieldArray>
    </Modal>
  )
}

const CompanyLinksEditModal = ({ handleClose, handleCancel }: Props) => {
  const { data } = useQuery(CompanyLinksHomeWidgetDocument, { fetchPolicy: 'cache-only' })

  const companyLinks = data?.dashboard.companyLinks ?? []

  const defaultValues: FormValues = {
    links: companyLinks.map(({ id, name, url }) => ({ id: String(id), name, url })),
    linkIdsToBeDeleted: [],
  }

  return (
    <FormProvider
      defaultValues={defaultValues}
      onSubmit={async (submitData) => {
        const { success, value } = await asyncAction(() =>
          companyLinksCollection.bulkUpdate({
            links: submitData.links.map(({ id, name, url, draft }, index) => ({
              id: draft ? undefined : id,
              name,
              url,
              order: index,
            })),
            linkIdsToBeDeleted: submitData.linkIdsToBeDeleted,
          })
        )

        if (success) {
          handleClose()
        }

        return Promise.resolve({ success: true, data: value })
      }}
    >
      <Content handleCancel={handleCancel} />
    </FormProvider>
  )
}

export default CompanyLinksEditModal
